import { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent } from '../ui/dialog';
import { Calendar } from '../ui/calendar';
import { getAvailableSlots, getMonthAvailability, formatSlotTime, rescheduleBookingById } from '@/services/integrations/cal';
import { formatDateString } from '@/services/integrations/cal/calDateHelpers';
import type { CalSlot } from '@/types/scheduling';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

// Format une tranche horaire (début - fin)
const formatTimeRange = (start: string, end: string): string => {
  return `${formatSlotTime(start)} - ${formatSlotTime(end)}`;
};

/**
 * Modal de reprogrammation d'un rendez-vous
 * 
 * FONCTIONNALITÉS :
 * - Affiche un calendrier avec les dates disponibles
 * - Charge dynamiquement les créneaux pour la date sélectionnée
 * - Gère l'invalidation du cache pour l'ancienne et la nouvelle date
 * - Gère le cas où le booking est déjà annulé avec message approprié
 * 
 * COMPORTEMENT :
 * - Auto-scroll vers le créneau sélectionné
 * - Rafraîchit l'UI après succès
 * - Toast de confirmation/erreur
 * 
 * RÈGLES MÉTIER :
 * - Ne peut reprogrammer que des événements non annulés
 * - Invalide le cache des anciennes et nouvelles dates automatiquement
 * - Synchronise automatiquement l'UI si le booking est déjà annulé
 * 
 * @param bookingId - ID de la réservation à reprogrammer (Cal.com ou Calendly)
 * @param currentDate - Date actuelle de la réservation
 * @param eventTitle - Titre de l'événement affiché dans le header
 * @param productId - ID du produit pour récupérer les créneaux disponibles
 * @param userId - ID de l'utilisateur pour filtrer les créneaux
 * @param locationId - ID optionnel de la location
 * @param isOpen - État d'ouverture du modal
 * @param onClose - Callback de fermeture
 * @param onSuccess - Callback appelé après reprogrammation réussie
 * 
 * @example
 * <RescheduleModal
 *   bookingId="uuid"
 *   currentDate={new Date('2025-01-15')}
 *   eventTitle="Tournage - Présence"
 *   productId="uuid"
 *   userId="uuid"
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   onSuccess={() => refreshCalendar()}
 * />
 */
interface RescheduleModalProps {
  bookingId: string;
  currentDate: Date;
  eventTitle: string;
  productId: string;
  userId: string;
  locationId?: string;
  eventType: 'shooting' | 'preparation' | 'editorial';
  relatedShootingDate?: Date;
  linkedEditorialBookingDate?: Date;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function RescheduleModal({
  bookingId,
  currentDate,
  eventTitle,
  productId,
  userId,
  locationId,
  eventType,
  relatedShootingDate,
  linkedEditorialBookingDate,
  isOpen,
  onClose,
  onSuccess,
}: RescheduleModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(currentDate);
  const [availableSlots, setAvailableSlots] = useState<CalSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<CalSlot | null>(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [datesWithAvailability, setDatesWithAvailability] = useState<Set<string>>(new Set());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  // Charger les disponibilités du mois pour le calendrier
  useEffect(() => {
    const loadMonthAvailability = async () => {
      try {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const availability = await getMonthAvailability(year, month, userId, productId, locationId, eventType);
        setDatesWithAvailability(availability);
      } catch (error) {
        logger.error('Error loading month availability', error);
      }
    };

    loadMonthAvailability();
  }, [currentMonth, userId, productId, locationId, eventType]);

  // Charger les créneaux disponibles pour la date sélectionnée
  useEffect(() => {
    const loadSlots = async () => {
      if (!selectedDate) return;

      setIsLoadingSlots(true);
      setSelectedSlot(null);

      try {
        const result = await getAvailableSlots(selectedDate, userId, productId, locationId, eventType);
        
        if (result.success && result.slots) {
          setAvailableSlots(result.slots);
        } else {
          console.error('Erreur lors du chargement des créneaux:', result.error);
          setAvailableSlots([]);
        }
      } catch (error) {
        console.error('Exception lors du chargement des créneaux:', error);
        setAvailableSlots([]);
      } finally {
        setIsLoadingSlots(false);
      }
    };

    loadSlots();
  }, [selectedDate, userId, productId, locationId, eventType]);

  const handleReschedule = async () => {
    if (!selectedSlot) return;

    setIsRescheduling(true);
    try {
      const result = await rescheduleBookingById(
        bookingId,
        selectedSlot.start,
        'Reprogrammation demandée par le client'
      );

      if (result.success) {
        toast.success('Rendez-vous reprogrammé', {
          description: `Votre rendez-vous a été déplacé au ${new Date(selectedSlot.start).toLocaleDateString('fr-FR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })} de ${formatTimeRange(selectedSlot.start, selectedSlot.end)}`,
        });
        onSuccess();
        onClose();
        return;
      } else {
        // Message plus clair selon le type d'erreur
        const isCancelled = result.error?.includes('annulé') || result.error?.includes('cancelled');
        toast.error(isCancelled ? 'Impossible de reprogrammer' : 'Erreur lors de la reprogrammation', {
          description: result.error || 'Une erreur est survenue',
        });
        
        // Si annulé, rafraîchir pour que l'UI soit à jour
        if (isCancelled) {
          onSuccess();
          onClose();
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la reprogrammation', {
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
      });
    } finally {
      setIsRescheduling(false);
    }
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) return true;

    if ((eventType === 'preparation' || eventType === 'editorial') && relatedShootingDate) {
      const shootingDate = new Date(relatedShootingDate);
      shootingDate.setHours(0, 0, 0, 0);
      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);
      if (compareDate >= shootingDate) return true;
    }

    if (eventType === 'shooting' && linkedEditorialBookingDate) {
      const editorialDate = new Date(linkedEditorialBookingDate);
      editorialDate.setHours(0, 0, 0, 0);
      const minShootingDate = new Date(editorialDate);
      minShootingDate.setDate(minShootingDate.getDate() + 1);
      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);
      if (compareDate < minShootingDate) return true;
    }

    const dateStr = formatDateString(date);
    return !datesWithAvailability.has(dateStr);
  };

  // Render the time slots section based on loading and availability state
  const renderSlotsSection = () => {
    if (isLoadingSlots) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-warning" />
        </div>
      );
    }

    if (availableSlots.length > 0) {
      return (
        <div className="flex flex-col gap-2">
          {availableSlots.map((slot) => (
            <button
              key={slot.start}
              onClick={() => setSelectedSlot(slot)}
              className={`glass rounded-lg py-2 px-3 text-sm text-center transition-all border ${
                selectedSlot?.start === slot.start
                  ? 'border-warning/50 bg-warning/20 text-warning'
                  : 'border-white/10 text-white/70 hover:border-warning/30 hover:text-white'
              }`}
            >
              {formatTimeRange(slot.start, slot.end)}
            </button>
          ))}
        </div>
      );
    }

    return (
      <p className="text-sm text-white/40 text-center py-8">
        Aucun créneau disponible pour cette date
      </p>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-screen h-screen max-w-none p-0 bg-black/95 border-0 text-white overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="p-6 min-h-full flex flex-col"
        >
          {/* Header */}
          <div className="space-y-4 mb-6">
            <div>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-warning/90">
                <CalendarIcon className="h-3 w-3" />
                <span>Reprogrammer</span>
              </div>
            </div>
          </div>

          {/* Calendrier */}
          <div className="flex-1 flex flex-col">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              disabled={isDateDisabled}
              onMonthChange={setCurrentMonth}
              className="rounded-md border border-white/10 mx-auto"
            />
          </div>

          {/* Créneaux horaires */}
          {selectedDate && (
            <div className="mt-6 space-y-3 flex-1">
              <div className="flex items-center gap-2 text-sm text-white/60">
                <Clock className="h-4 w-4" />
                <span>
                  {selectedDate.toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })}
                </span>
              </div>

              {renderSlotsSection()}
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex gap-3 sticky bottom-0 bg-black/95 pt-4 pb-2">
            <button
              onClick={onClose}
              disabled={isRescheduling}
              className="flex-1 glass rounded-xl py-3 px-4 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-all border border-white/10 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={handleReschedule}
              disabled={!selectedSlot || isRescheduling}
              className="flex-1 glass rounded-xl py-3 px-4 text-sm text-warning hover:text-warning/80 hover:bg-warning/10 transition-all border border-warning/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isRescheduling ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Reprogrammation...</span>
                </>
              ) : (
                'Confirmer'
              )}
            </button>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
